// [Basis chrome] Tabla de enlaces del chrome del explorador.
//
// Espejo de frontend_landing/src/consts.ts y de los menus de AppHeader.astro /
// Footer.astro. Se escribe una vez aqui y la consumen la navbar y el footer.
// Etiquetas y rutas salen del marcado que compila la landing (es), no se
// reescriben: si la landing cambia un rotulo, se copia el nuevo.
//
// IDIOMA: la landing publica cinco (es/en/pt/fr/de) y desde e7e40f6 TODAS con
// prefijo --«ninguna direccion sin prefijo es una pagina»--, resolviendolo con
// getLocalizedPath(). Blockscout no tiene i18n -- sus propios rotulos de
// navegacion estan escritos en ingles a pelo en lib/hooks/useNavItems.tsx -- asi
// que aqui se apunta siempre a /es/, el idioma en el que ya estan escritos
// brand/assets/footer-links.json y NEXT_PUBLIC_OG_DESCRIPTION. Con barra final,
// como las sirve la landing.
//
// DOMINIO: absoluto y no relativo. El explorador vive en
// explorer.basisnetwork.com.co y la landing en basisnetwork.com.co: son origenes
// distintos y un href relativo caeria dentro del propio explorador.

export const LANDING = 'https://basisnetwork.com.co';

// La raiz de las paginas en espanol y la portada, a la que lleva la marca.
const ES = `${ LANDING }/es`;
export const LANDING_HOME = `${ ES }/`;

export const CONTACT_EMAIL = 'contact@basisnetwork.com.co';
export const DISCORD_URL = 'https://discord.gg/7ZKkqEpWj2';
export const LINKEDIN_URL = 'https://www.linkedin.com/company/basis-network/';
export const INSTAGRAM_URL = 'https://www.instagram.com/basis_network/';
export const X_URL = 'https://x.com/basis_network_';
export const STATUS_URL = 'https://status.basisnetwork.com.co';
export const FAUCET_URL = 'https://faucet.basisnetwork.com.co';

// La organizacion de GitHub, creada el 2026-08-24: los dos repositorios
// publicos. Es la quinta red del pie y la unica tecnica.
export const GITHUB_URL = 'https://github.com/basis-network';

export interface BasisLink {
  text: string;
  url: string;
  // Rotulo del pie cuando difiere del de la cabecera (la landing escribe
  // «Preguntas» en el menu y «Preguntas frecuentes» en el pie). Sin el, el pie
  // usa `text`.
  footerText?: string;
}

// AppHeader.astro -> productLinks. La prueba de auditoria va la primera --es la
// entrada que la landing quiere que se pulse-- y «En produccion» ya no existe.
export const PRODUCT_LINKS: Array<BasisLink> = [
  { text: 'Test de auditoría', url: `${ ES }/diagnostico/` },
  { text: 'Clientes', url: `${ ES }/#customers` },
  { text: 'Cómo funciona', url: `${ ES }/#how_it_works` },
  { text: 'Sectores', url: `${ ES }/#sectors` },
  { text: 'Por qué Basis', url: `${ ES }/#why_basis` },
  { text: 'Preguntas', footerText: 'Preguntas frecuentes', url: `${ ES }/#faq` },
];

// AppHeader.astro -> projectLinks
export const PROJECT_LINKS: Array<BasisLink> = [
  { text: 'Manifiesto', url: `${ ES }/manifiesto/` },
  { text: 'Economía', url: `${ ES }/economia/` },
  { text: 'Gobernanza', url: `${ ES }/gobernanza/` },
];

// El CTA principal de la landing lleva al calendario de agendamiento.
export const SCHEDULE_URL = `${ ES }/agenda/`;
export const SCHEDULE_LABEL = 'Agenda 20 minutos';

// Footer.astro -> columna «Recursos». Solo lo que la landing pone ahi: cuando
// nacio «Para desarrolladores», «Estado de la red» se mudo a esa columna.
export const RESOURCE_LINKS: Array<BasisLink> = [
  { text: 'Blog', url: `${ ES }/blog/` },
  { text: 'Novedades', url: `${ ES }/novedades/` },
];

// Footer.astro -> columna «Para desarrolladores».
//
// Faucet, estado y RPC son los de la landing, en su orden, y «Código en GitHub»
// la cierra, como alli. Falta «Explorador»: esta pagina ES el explorador y
// enlazarse a si misma no le sirve a nadie. En su lugar van los dos endpoints
// propios --API REST y GraphQL-- que la landing no tiene por que conocer y que
// antes vivian sueltos dentro de «Recursos».
//
// Siguen siendo los mismos que publica brand/assets/footer-links.json, que este
// chrome sustituye: cuando el footer lo pinta React, NEXT_PUBLIC_FOOTER_LINKS
// deja de leerse.
// «Acceso al RPC» apunta a /es/docs/ de la landing: la documentacion de la
// devnet vive alli, no en el dominio del explorador. El RPC en si no se enlaza:
// cada peticion necesita un token nominal y esa pagina explica como pedirlo.
export const DEVNET_LINKS: Array<BasisLink> = [
  { text: 'Faucet de LITHOS', url: FAUCET_URL },
  { text: 'Estado de la red', url: STATUS_URL },
  { text: 'Acceso al RPC', url: `${ ES }/docs/` },
  { text: 'API REST', url: '/api-docs' },
  { text: 'GraphQL', url: '/api-docs?tab=graphql_api' },
  { text: 'Código en GitHub', url: GITHUB_URL },
];

export const LEGAL_LINKS = {
  privacy: `${ ES }/legal/privacy/`,
  cookies: `${ ES }/legal/cookies/`,
};

export const TAGLINE =
  'Registros que nadie puede cambiar, para llegar a cada auditoría con todo en regla. Primera L1 colombiana.';

export const LOCATION = 'Ruta N, Medellín, Colombia';

export const RIGHTS = 'Basis Network. Todos los derechos reservados.';

export const CREDENTIALS_EYEBROW = 'Respaldo y credenciales';

// Nota de marcas, palabra por palabra desde src/i18n/es.json (credentials.trademarkNote).
// No se resume: es el descargo que acompana a los logos de terceros.
export const TRADEMARK_NOTE =
  'Avalanche® es marca de Ava Labs, Inc.; Google Cloud y Google for Startups Cloud Program, ' +
  'de Google LLC; Ruta N y Gen N, de la Corporación Ruta N Medellín. Estos logos identifican ' +
  'programas y reconocimientos obtenidos por Basis Network: no implican sociedad, alianza, ' +
  'inversión ni patrocinio, y ninguna de estas organizaciones ha aprobado ni certificado la ' +
  'tecnología de Basis. Basis es una L1 independiente y no está construida sobre Avalanche.';

export const CERTIFICATIONS_EYEBROW = 'Certificaciones';

// Los cinco logos de certificacion, espejo de la tabla `certifications` de
// Footer.astro en la landing. Eran tres hasta el 2026-08-25, cuando OpenChain
// aprobo la conformidad con ISO/IEC 5230 y con ISO/IEC 18974 y entraron sus dos
// placas.
//
// Son EL ARTE DEL EMISOR, servido desde public/basis-chrome/. Ni redibujado ni
// blanqueado, al reves que los logos de respaldo de al lado: un respaldo lo
// concede una institucion y se subordina a la maqueta; una certificacion se
// gana contra un criterio publico y llega con su sello puesto.
//
// No son las etiquetas de shields del README, y no por gusto: la de Scorecard
// hornea la nota --el 2026-08-24 servia 5.8 mientras la API en vivo devolvia
// 7.3 para el mismo commit-- y las tres, enlazadas a su dominio, filtrarian la
// IP de cada visitante a bestpractices.dev, shields.io y reuse.software. La
// nota de marca entera, con la procedencia y la tinta de cada fichero, esta en
// Footer.astro de la landing, que es donde se decidio.
//
// `displayHeight` va por PESO OPTICO, no por caja: los dos emblemas a 56 px y
// el lockup de REUSE a 26, porque un logotipo con letras pesa mucho mas que un
// escudo a la misma altura.
//
// OJO con el enlace de REUSE: hoy responde 424 porque su rastreador aun no ha
// pasado por el repositorio. Se enlaza a sabiendas, es la direccion definitiva
// y se arregla sola.
//
// LAS DOS DE OPENCHAIN COMPARTEN `href`, y no es un descuido: OpenChain no
// publica pagina por organizacion ni informe por repositorio. Su registro
// publico es una sola lista con dos secciones --una por norma-- y «Basis
// Network» sale en las dos. La pagina no tiene anclas, asi que no hay forma de
// llevar cada placa a su mitad. `key` en BasisFooter va por `alt`, que si es
// distinto, no por `href`.
//
// Y SUS DOS PNG NO SON LOS QUE UNO DESCARGA DE OPENCHAIN. La placa es line-art
// dibujado para fondo blanco: su aro mide 4 px sobre 500 en el original, o sea
// menos de medio pixel a los 56 de esta fila, y a ese tamano el remuestreo no
// dibuja una linea sino un gris translucido que sobre --surface-night no se
// distingue del fondo. Los ficheros de public/basis-chrome/ llevan el trazo
// engrosado 4 px; lo hace `.docs/archivo/engrosar-placa-openchain.py` de la
// carpeta de trabajo. Volver a copiarlas del origen deshace el arreglo y no lo
// avisa nadie: el componente sigue montando y la imagen sigue cargando.
export interface BasisCertification {
  src: string;
  alt: string;
  href: string;
  width: number;
  height: number;
  displayHeight: string;
}

export const CERTIFICATIONS: Array<BasisCertification> = [
  {
    src: '/basis-chrome/openssf-best-practices.png',
    alt: 'OpenSSF Best Practices',
    href: 'https://www.bestpractices.dev/projects/14224',
    width: 168, height: 168, displayHeight: '56px',
  },
  {
    src: '/basis-chrome/openssf-scorecard.png',
    alt: 'OpenSSF Scorecard',
    href: 'https://scorecard.dev/viewer/?uri=github.com/basis-network/basis-cli',
    width: 99, height: 168, displayHeight: '56px',
  },
  {
    src: '/basis-chrome/openchain-iso-5230.png',
    alt: 'OpenChain ISO/IEC 5230 — Open Source License Compliance Program',
    href: 'https://openchainproject.org/community-of-conformance',
    width: 156, height: 168, displayHeight: '56px',
  },
  {
    src: '/basis-chrome/openchain-iso-18974.png',
    alt: 'OpenChain ISO/IEC 18974 — Open Source Security Assurance Program',
    href: 'https://openchainproject.org/community-of-conformance',
    width: 157, height: 168, displayHeight: '56px',
  },
  {
    src: '/basis-chrome/reuse.png',
    alt: 'REUSE Software',
    href: 'https://api.reuse.software/info/github.com/basis-network/basis-cli',
    width: 223, height: 52, displayHeight: '26px',
  },
];
